import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { queryClient } from "./lib/queryClient";
import Index from "./pages/Index";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ResetPassword from "./pages/ResetPassword";
import EmailConfirmation from "./pages/EmailConfirmation";
import EmailVerified from "./pages/EmailVerified";
import LegacyTenantRedirect from "./components/LegacyTenantRedirect";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";
import TestPayments from "./pages/TestPayments";
import AuthCallback from "./components/AuthCallback";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthRedirect } from "./components/AuthRedirect";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { DebugNavigationTracker } from "./components/DebugNavigationTracker";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import Install from "./pages/Install";

// Lazy load heavy routes for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const TenantPortal = lazy(() => import("./pages/TenantPortal"));
const DocumentSigning = lazy(() => import("./pages/DocumentSigning"));
const MaintenanceRequest = lazy(() => import("./pages/MaintenanceRequest"));
const Booking = lazy(() => import("./pages/Booking"));
const BookingPage = lazy(() => import("./pages/BookingPage").then(module => ({ default: module.BookingPage })));
const PublicBooking = lazy(() => import("./pages/PublicBooking").then(module => ({ default: module.PublicBooking })));

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ErrorBoundary>
          <AuthProvider>
            <CurrencyProvider>
              <SubscriptionProvider>
                <DebugNavigationTracker />
                <PWAInstallPrompt />
                <Suspense fallback={
                  <div className="min-h-screen flex items-center justify-center bg-background">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                  </div>
                }>
                  <Routes>
                {/* Auth callback route - must be before other routes */}
                <Route path="/auth/callback" element={<AuthCallback />} />
                
                {/* Landlord Platform Routes */}
                <Route path="/" element={<AuthRedirect><Index /></AuthRedirect>} />
                <Route path="/login" element={<AuthRedirect><Login /></AuthRedirect>} />
                <Route path="/signup" element={<AuthRedirect><SignUp /></AuthRedirect>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/email-confirmation" element={<EmailConfirmation />} />
                <Route path="/email-sent" element={<EmailConfirmation />} />
                <Route path="/email-verified" element={<EmailVerified />} />
                <Route path="/maintenance-request/:token" element={<MaintenanceRequest />} />
                <Route path="/booking/:token" element={<PublicBooking />} />
                <Route path="/booking-page/:id" element={<BookingPage />} />
                <Route path="/book/:token" element={<PublicBooking />} />
                <Route path="/sign/:token" element={<DocumentSigning />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancel" element={<PaymentCancel />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/install" element={<Install />} />
                <Route path="/test-payments" element={<TestPayments />} />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute requireRole="landlord">
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Tenant Portal Routes */}
                <Route path="/tenant-portal/*" element={<TenantPortal />} />
                
                {/* Legacy redirects for existing tenant links */}
                <Route path="/tenant-signup/:token" element={<LegacyTenantRedirect />} />
                <Route path="/tenant-onboarding/:token" element={<LegacyTenantRedirect />} />
                <Route path="/tenant-signing/:token" element={<LegacyTenantRedirect />} />
                <Route path="/tenant-dashboard" element={<LegacyTenantRedirect />} />
                
                <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </SubscriptionProvider>
            </CurrencyProvider>
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
